export const ONBOARDING_CHANNEL_IDS = [
  "hire_builder",
  "agent_profile",
  "website",
  "email",
  "social",
  "partner",
] as const;

export type OnboardingSessionChannel = (typeof ONBOARDING_CHANNEL_IDS)[number];
export type OnboardingChannelFilter = "all" | OnboardingSessionChannel;

export const ONBOARDING_CHANNEL_META: Record<
  OnboardingSessionChannel,
  { label: string; shortLabel: string; description: string }
> = {
  hire_builder: {
    label: "Hire-builder",
    shortLabel: "Hire-builder",
    description: "Direct hire-builder chat sessions",
  },
  agent_profile: {
    label: "Agent Profile",
    shortLabel: "Profile",
    description: "Started from the public Agent Profile widget",
  },
  website: {
    label: "Website",
    shortLabel: "Website",
    description: "Embedded website or landing page links",
  },
  email: {
    label: "Email",
    shortLabel: "Email",
    description: "Email campaigns and nurture flows",
  },
  social: {
    label: "Social",
    shortLabel: "Social",
    description: "Social posts, bios, and ad landing paths",
  },
  partner: {
    label: "Partner",
    shortLabel: "Partner",
    description: "White-label partner and co-branded flows",
  },
};

export const DEFAULT_ONBOARDING_CHANNEL: OnboardingSessionChannel = "hire_builder";

export function isOnboardingSessionChannel(value: unknown): value is OnboardingSessionChannel {
  return (
    typeof value === "string" &&
    (ONBOARDING_CHANNEL_IDS as readonly string[]).includes(value)
  );
}

export function resolveOnboardingChannelFromQuery(
  params: Pick<URLSearchParams, "get"> | null | undefined,
): OnboardingSessionChannel {
  if (!params) return DEFAULT_ONBOARDING_CHANNEL;

  const explicit = params.get("channel")?.trim().toLowerCase();
  if (isOnboardingSessionChannel(explicit)) return explicit;

  const utmSource = params.get("utm_source")?.trim().toLowerCase();
  if (utmSource === "email") return "email";
  if (utmSource === "social" || utmSource === "linkedin" || utmSource === "facebook") {
    return "social";
  }
  if (utmSource === "partner") return "partner";
  if (utmSource === "profile" || utmSource === "agent_profile") return "agent_profile";
  if (utmSource === "website" || utmSource === "web") return "website";

  return DEFAULT_ONBOARDING_CHANNEL;
}

export function resolveOnboardingSessionChannel(metadata: unknown): OnboardingSessionChannel {
  if (!metadata || typeof metadata !== "object") return DEFAULT_ONBOARDING_CHANNEL;
  const channel = (metadata as Record<string, unknown>).channel;
  return isOnboardingSessionChannel(channel) ? channel : DEFAULT_ONBOARDING_CHANNEL;
}

export function getOnboardingChannelLabel(channel: OnboardingSessionChannel): string {
  return ONBOARDING_CHANNEL_META[channel].label;
}

export type OnboardingSessionRow = {
  id: string;
  status: string;
  channel: OnboardingSessionChannel;
};

export function countOnboardingSessionsByChannel<T extends OnboardingSessionRow>(
  sessions: T[],
): Record<OnboardingSessionChannel, number> {
  const counts = Object.fromEntries(
    ONBOARDING_CHANNEL_IDS.map((id) => [id, 0]),
  ) as Record<OnboardingSessionChannel, number>;

  for (const session of sessions) {
    counts[session.channel] += 1;
  }

  return counts;
}

export function filterOnboardingSessionsByChannel<T extends OnboardingSessionRow>(
  sessions: T[],
  channel: OnboardingChannelFilter,
): T[] {
  if (channel === "all") return sessions;
  return sessions.filter((session) => session.channel === channel);
}

export function filterOnboardingSessionsByStatus<T extends { status: string }>(
  sessions: T[],
  statusFilter: "all" | "started" | "completed" | "signed_up",
): T[] {
  if (statusFilter === "all") return sessions;
  return sessions.filter((session) => session.status === statusFilter);
}
